import { Button } from "@win-reality/react-ui";
import { useState } from "react";

import { HorizontalMenu } from "../../../../../components/ui/HorizontalMenu";
import { ContactVis } from "./visualizations/ContactVis";
import { LandingVis } from "./visualizations/LandingVis";
import { ReleaseVis } from "./visualizations/ReleaseVis";
import { StrikeZone } from "./visualizations/StrikeZone";

interface Props {
  withHittingOptions?: boolean;
  containerHeight?: string;
  containerWidth?: string;
  releasePointVisStyle?: React.CSSProperties;
}

/**
 * VisualizationContainer - Main container for pitch and hit visualizations
 *
 * Features:
 * - StrikeZone visualization with pitch plotting
 * - Landing point visualization (hit landing location)
 * - Contact point visualization (bat-ball contact)
 * - Release point visualization (pitcher release)
 * - Switcher to toggle between visualizations
 * - Pitcher/Catcher perspective toggle (for strike zone)
 * - 2-strike zone toggle (expanded strike zone)
 *
 * Phase 9: Full visualization implementation
 */
export const VisualizationContainer = ({
  withHittingOptions,
  containerHeight,
  containerWidth,
  releasePointVisStyle,
}: Props) => {
  const [activeVis, setActiveVis] = useState("StrikeZone");

  // LocalStorage for strike zone perspective (catcher vs pitcher view)
  const [pitcherPerspective, setPitcherPerspective] = useState<
    "pitcher" | "catcher"
  >(() => {
    const stored = localStorage.getItem("strike-zone-perspective");
    return stored === "pitcher" ? "pitcher" : "catcher";
  });

  // LocalStorage for 2-strike zone toggle
  const [twoStrikeZone, setTwoStrikeZone] = useState<boolean>(() => {
    const stored = localStorage.getItem("two-strike-zone");
    return stored === "on";
  });

  const handlePerspectiveToggle = () => {
    const newPerspective =
      pitcherPerspective === "pitcher" ? "catcher" : "pitcher";
    setPitcherPerspective(newPerspective);
    localStorage.setItem("strike-zone-perspective", newPerspective);
  };

  const handleTwoStrikeZoneToggle = () => {
    const newValue = !twoStrikeZone;
    setTwoStrikeZone(newValue);
    localStorage.setItem("two-strike-zone", newValue ? "on" : "off");
  };

  return (
    <div
      className="relative flex min-w-[360px] flex-col items-center justify-center rounded bg-white shadow-md"
      style={{
        width: containerWidth || "25%",
        height: containerHeight || "382px",
      }}
    >
      {/* Visualization Switcher */}
      <div className="absolute top-2.5 w-full bg-white">
        <div className="flex justify-center">
          <HorizontalMenu
            items={[
              {
                label: "Strike Zone",
                active: activeVis === "StrikeZone",
                onClick: () => setActiveVis("StrikeZone"),
              },
              {
                label: "Release",
                active: activeVis === "Release",
                onClick: () => setActiveVis("Release"),
              },
              ...(withHittingOptions
                ? [
                    {
                      label: "Contact",
                      active: activeVis === "Contact",
                      onClick: () => setActiveVis("Contact"),
                    },
                    {
                      label: "Landing",
                      active: activeVis === "Landing",
                      onClick: () => setActiveVis("Landing"),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>

      {/* Visualizations */}
      {activeVis === "StrikeZone" && (
        <StrikeZone
          perspective={pitcherPerspective}
          twoStrikeZone={twoStrikeZone}
        />
      )}
      {activeVis === "Landing" && <LandingVis />}
      {activeVis === "Contact" && <ContactVis />}
      {activeVis === "Release" && <ReleaseVis style={releasePointVisStyle} />}

      {/* Perspective Toggle (Strike Zone only) */}
      {activeVis === "StrikeZone" && (
        <div className="absolute bottom-0 right-0 p-0.5">
          <Button
            size="xs"
            variant="ghost"
            className="normal-case border border-gray-300"
            onClick={handlePerspectiveToggle}
          >
            {pitcherPerspective === "pitcher"
              ? "Pitcher's POV"
              : "Catcher's POV"}
          </Button>
        </div>
      )}

      {/* 2-Strike Zone Toggle (Strike Zone only) */}
      {activeVis === "StrikeZone" && (
        <div className="absolute bottom-0 left-0 p-0.5">
          <Button
            size="xs"
            variant="ghost"
            className="normal-case border border-gray-300"
            onClick={handleTwoStrikeZoneToggle}
          >
            2 Strike Zone
          </Button>
        </div>
      )}
    </div>
  );
};
